'use client';

import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Fade,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import isEqual from 'lodash/isEqual';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../../context/EstimatorContext';
import { calculateClassLengths } from '../../../utils/hiddenPipeCostCalculator';
import { calculateNDEPricing } from '../../../utils/hiddenNDECalculator';
import { costCategoryCalculator } from '../../../utils/costCategoryCalculator';
import useFetchSnapshotRowPricing from '../../../hooks/pricingSnapshot/useFetchSnapshotRowPricing';
import useFetchSnapshotPipePricing from '../../../hooks/pricingSnapshot/useFetchSnapshotPipePricing';
import useSaveCostCategorySection from '../../../hooks/useSaveCostCategorySection';
import useNotesDialog from '../../../hooks/useNotesDialog';

const initialRows = [
  {
    no: 1,
    account: '141-01-1001',
    category: 'Survey, Title, ROW',
    description: 'ROW Lease Fees',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    notes: '',
    totalPrice: '$',
  },
  {
    no: 2,
    account: '141-01-1001',
    category: 'Survey, Title, ROW',
    description: 'Access fees (roads)',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    notes: '',
    totalPrice: '$',
  },
  {
    no: 3,
    account: '141-01-1001',
    category: 'Survey, Title, ROW',
    description: 'Temporary Sites (bores, construction)',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 4,
    account: '141-01-1003',
    category: 'Survey, Title, ROW',
    description: 'ROW Agent Fees',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 5,
    account: '141-01-1003',
    category: 'Survey, Title, ROW',
    description: 'Title Agent Fees',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 6,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'Preliminary Survey',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 7,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'Plats, Alignment Sheets',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 8,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'As-Built Survey',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 9,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'Biological Survey',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 10,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'Archeological Survey',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 11,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'Class Location/HCA Study',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 12,
    account: '141-01-1004',
    category: 'Survey, Title, ROW',
    description: 'SWPPP Services',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 13,
    account: '141-01-1007',
    category: 'Survey, Title, ROW',
    description: 'Misc Permits and Fees',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 14,
    account: '',
    category: '',
    description: 'Survey, Title, ROW Total',
    pmOverride: '',
    price: '',
    contingencyPercent: '',
    contOverridePercent: '',
    contingency: '',
    contingencyCost: '',
    taxRate: '',
    taxCost: '',
    baseCost: '$',
    totalPrice: '$',
    isSummary: true,
  },
];

const columnDefs = [
  {
    headerName: 'Notes',
    field: 'notesIcons',
    cellRenderer: (params) => (
      <>
        <Tooltip title="View Note">
          <IconButton onClick={() => params.context.showNoteDialog(params.data, false)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Note">
          <IconButton onClick={() => params.context.showNoteDialog(params.data, true)}>
            <NoteAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    ),
    flex: 1,
    suppressMenu: true,
    sortable: false,
  },
  { headerName: 'Account', field: 'account', flex: 1 },
  { headerName: 'Category', field: 'category', flex: 1 },
  { headerName: 'Description', field: 'description', flex: 1 },
  {
    headerName: 'PM Override',
    field: 'pmOverride',
    editable: true,
    flex: 1,
    cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Price',
    field: 'price',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: '% Contingency',
    field: 'contingencyPercent',
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
    editable: false,
  },
  {
    headerName: '% Cont Override',
    field: 'contOverridePercent',
    editable: true,
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
    cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
  },
  {
    headerName: 'Contingency Cost',
    field: 'contingencyCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Tax Rate',
    field: 'taxRate',
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
  },
  {
    headerName: 'Tax Cost',
    field: 'taxCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Base Cost',
    field: 'baseCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Total Price',
    field: 'totalPrice',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
];

const SurveyTitleRow = () => {
  const { formData, setFormData, setSectionSummaries } = useEstimator();
  const [gridData, setGridData] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const skipEffectRef = useRef(false);

  const { noteDialogOpen, noteValue, setNoteValue, selectedRow, isEditing, showNoteDialog, resetDialog } =
    useNotesDialog();

  const estimateId = formData.estimateId;
  const { rowPricing } = useFetchSnapshotRowPricing(estimateId);
  const { pipePricing } = useFetchSnapshotPipePricing(estimateId);

  const { saveSection, loading, saved } = useSaveCostCategorySection();

  const handleNoteSave = () => {
    if (!selectedRow) return;
    const trimmed = noteValue.trim();
    const updatedRow = { ...selectedRow, notes: trimmed };

    const updatedData = gridData.map((r) => (r.no === selectedRow.no ? updatedRow : r));

    setGridData(updatedData);
    resetDialog();

    const estimateId = formData.id || formData.estimateId;
    saveSection(estimateId, 'Survey, Title, ROW', updatedData);
  };

  const handleSave = () => {
    const estimateId = formData.id || formData.estimateId;

    const updatedOverrides = gridData
      .filter((row) => row.no >= 1 && row.no <= 14)
      .map((row) => ({ ...row, rowNumber: row.no }));

    setFormData((prev) => ({
      ...prev,
      costCategory: updatedOverrides,
    }));

    saveSection(estimateId, 'Survey, Title, ROW', gridData);
  };

  useEffect(() => {
    if (skipEffectRef.current) {
      skipEffectRef.current = false;
      return;
    }

    if (!formData?.pipeSpecs || !formData?.quantities || !pipePricing?.length) return;

    // Step 1: Calculate all rows
    const updatedRows = initialRows.map((row) => {
      const override = formData.costCategory?.find((r) => r.rowNumber === row.no) || {};

      const rowWithOverrides = {
        ...row,
        pmOverride: override.pmOverride?.toString() ?? '',
        contOverridePercent: override.contOverridePercent?.toString() ?? '',
        notes: override.notes ?? '',
      };

      const calculator = costCategoryCalculator[row.no];
      if (calculator) {
        const calculated = calculator(rowWithOverrides, formData, rowPricing);
        return { ...rowWithOverrides, ...calculated };
      }

      return rowWithOverrides;
    });

    // Step 2: Calculate section total (rows 1–13 → row 14)
    let totalContingencyCost = 0;
    let totalBaseCost = 0;
    let totalTotalPrice = 0;

    for (const row of updatedRows) {
      if (row.no >= 1 && row.no <= 13) {
        totalContingencyCost += isNaN(parseFloat(row.contingencyCost)) ? 0 : parseFloat(row.contingencyCost);
        totalBaseCost += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
        totalTotalPrice += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
      }
    }

    const summaryIndex = updatedRows.findIndex((r) => r.no === 14);
    if (summaryIndex !== -1) {
      updatedRows[summaryIndex] = {
        ...updatedRows[summaryIndex],
        contingencyCost: totalContingencyCost,
        baseCost: totalBaseCost,
        totalPrice: totalTotalPrice,
        isSummary: true,
      };
    }

    // ✅ Step 3: Only update context if the value actually changed
    const newSummary14 = {
      contingencyCost: totalContingencyCost,
      baseCost: totalBaseCost,
      totalPrice: totalTotalPrice,
    };

    setSectionSummaries((prev) => {
      if (isEqual(prev[14], newSummary14)) return prev;
      return {
        ...prev,
        14: newSummary14,
      };
    });

    // Step 4: Update grid
    setGridData(updatedRows);

    // Step 5: Hidden pipe + NDE calculations
    const pipeODQuantity = formData.quantities?.[0]?.quantity || 0;
    const pipeLengthByClass = calculateClassLengths(formData.pipeSpecs, pipeODQuantity, pipePricing || []);
    const ndeCalculations = calculateNDEPricing(formData.quantities, formData.costCategory);

    setFormData({
      ...formData,
      hiddenPipeCostCalc: pipeLengthByClass,
      hiddenNDECalc: ndeCalculations,
    });
  }, [
    pipePricing,
    formData.pipeSpecs,
    formData.quantities,
    formData.costCategory,
    setSectionSummaries,
    formData.costAssumptions,
  ]);

  return (
    <Fade in>
      <Box mt={4} display="flex" flexDirection="column" gap={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ backgroundColor: '#8CA3C4', color: 'white', padding: '8px 16px', borderRadius: 1 }}
        >
          <Typography variant="h6">Cost Category Summary: Survey, Title, ROW</Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
          </Button>
        </Box>

        {!isCollapsed && (
          <Paper elevation={3} sx={{ p: 3, backgroundColor: '#F9F9F9', borderRadius: 2 }}>
            <Box className="ag-theme-alpine" sx={{ width: '100%', overflowX: 'auto', paddingBottom: 2 }}>
              <AgGridReact
                rowData={gridData}
                columnDefs={columnDefs}
                context={{ showNoteDialog }}
                defaultColDef={{ resizable: true, editable: true, minWidth: 120, flex: 1 }}
                getRowStyle={(params) => {
                  const rowNo = params.data?.no;
                  const hasNote = params.data?.notes && params.data.notes.trim() !== '';

                  // Style for summary row (row 14)
                  if (rowNo === 14) {
                    return {
                      backgroundColor: '#e3f2fd', // light blue
                      fontWeight: 'bold',
                    };
                  }

                  // Style for rows with notes
                  return {
                    backgroundColor: hasNote ? '#fffbe6' : undefined,
                    fontWeight: params.data?.isSummary ? 'bold' : undefined,
                  };
                }}
                domLayout="autoHeight"
                onCellValueChanged={(params) => {
                  const { colDef, data, newValue } = params;
                  const field = colDef.field;

                  if (field === 'pmOverride' || field === 'contOverridePercent') {
                    const updatedCostCategory = [...(formData.costCategory || [])];
                    const index = updatedCostCategory.findIndex((r) => r.rowNumber === data.no);

                    if (index >= 0) {
                      updatedCostCategory[index] = { ...updatedCostCategory[index], [field]: newValue };
                    } else {
                      updatedCostCategory.push({ rowNumber: data.no, [field]: newValue });
                    }

                    skipEffectRef.current = true;
                    setFormData({ ...formData, costCategory: updatedCostCategory });

                    const updatedRowInput = { ...data, [field]: newValue };
                    const calculator = costCategoryCalculator[data.no];
                    let updatedRow = { ...updatedRowInput };

                    if (calculator) {
                      const newCalculated = calculator(
                        updatedRowInput,
                        { ...formData, costCategory: updatedCostCategory },
                        rowPricing
                      );
                      updatedRow = { ...updatedRowInput, ...newCalculated };
                    }

                    // Update full grid data including the summary
                    setGridData((prev) => {
                      const newGrid = prev.map((r) => (r.no === data.no ? updatedRow : r));

                      // Re-sum rows 1–13 and update row 14
                      let totalContingencyCost = 0;
                      let totalBaseCost = 0;
                      let totalTotalPrice = 0;

                      for (const row of newGrid) {
                        if (row.no >= 1 && row.no <= 13) {
                          totalContingencyCost += isNaN(parseFloat(row.contingencyCost))
                            ? 0
                            : parseFloat(row.contingencyCost);
                          totalBaseCost += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
                          totalTotalPrice += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
                        }
                      }

                      const summaryIndex = newGrid.findIndex((r) => r.no === 14);
                      if (summaryIndex !== -1) {
                        newGrid[summaryIndex] = {
                          ...newGrid[summaryIndex],
                          contingencyCost: totalContingencyCost,
                          baseCost: totalBaseCost,
                          totalPrice: totalTotalPrice,
                          isSummary: true,
                        };
                      }

                      return newGrid;
                    });
                  }
                }}
              />
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : saved ? 'Saved!' : 'Save'}
              </Button>
            </Box>
          </Paper>
        )}

        <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditing ? 'Edit Note' : 'View Note'}</DialogTitle>
          <DialogContent>
            <TextField
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              InputProps={{ readOnly: !isEditing }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
            {isEditing && (
              <Button variant="contained" onClick={handleNoteSave}>
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default SurveyTitleRow;
